import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { type FC, useState } from "react";

import { Badge, Button, Small } from "@fronterahealth/frontera-ui-components";

import { notifySuccess } from "@components/notifications/notifications";
import { CategoryRadio, PayorTypeMultiSelect } from "@pages/QASettings/BaseQAPackageForm";
import { KebabMenu, type MenuItem, RULE_GRID, RuleItem } from "@pages/QASettings/QARuleEditor";
import { PackageType, type QAPackage, type QARule } from "@pages/QASettings/baseQaTypes";
import { ReportCategory, type ReportCategoryValue } from "@pages/QASettings/constants";
import { type ReportType, serializeRuleBody } from "@pages/QASettings/qaRuleBlob";
import { type EditableRuleFields, deriveFields, toggleReportType } from "@pages/QASettings/qaRuleFields";
import { reportCategoryFromTypes, reportTypesFromCategory } from "@pages/QASettings/reportCategory";

// ---------------------------------------------------------------------------
// Package card
// ---------------------------------------------------------------------------

const TYPE_BADGE: Record<QAPackage["package_type"], { label: string; appearance: "neutral" | "processing" }> = {
  [PackageType.BASE]: { label: "Base", appearance: "neutral" },
  [PackageType.INSURANCE]: { label: "Insurance", appearance: "processing" },
};

const CATEGORY_BADGE: Record<ReportCategoryValue, { label: string; appearance: "warn" | "success" }> = {
  [ReportCategory.DIAGNOSIS]: { label: "Diagnosis", appearance: "warn" },
  [ReportCategory.ABA]: { label: "ABA", appearance: "success" },
};

export interface BaseQAPackageCardProps {
  pkg: QAPackage;
  payorTypes: string[];
  onUpdate: (args: {
    id: number;
    name?: string;
    description?: string;
    instructions?: string;
    payor_types?: string[];
    report_types?: string[];
  }) => Promise<unknown>;
  onDuplicate: (id: number) => Promise<unknown>;
  onDelete: (id: number) => Promise<unknown>;
  onToggleGlobal: (args: { id: number; isGloballyDisabled: boolean }) => Promise<unknown>;
  onAddRule: (packageId: number) => Promise<unknown>;
  onUpdateRule: (id: number, rule?: string, instructions?: string) => Promise<unknown>;
  onDeleteRule: (id: number) => Promise<unknown>;
}

export const BaseQAPackageCard: FC<BaseQAPackageCardProps> = ({
  pkg,
  payorTypes,
  onUpdate,
  onDuplicate,
  onDelete,
  onToggleGlobal,
  onAddRule,
  onUpdateRule,
  onDeleteRule,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(pkg.name);
  const [editDescription, setEditDescription] = useState(pkg.description);
  const [editInstructions, setEditInstructions] = useState(pkg.instructions);
  const [editPayorTypes, setEditPayorTypes] = useState<string[]>(pkg.payor_types);
  const [editCategory, setEditCategory] = useState<ReportCategoryValue>(reportCategoryFromTypes(pkg.report_types));

  const [expandedRuleIds, setExpandedRuleIds] = useState<Set<number>>(() => new Set());
  // Local draft of a rule's edited fields; committed to the server on blur
  // (or immediately for chip toggles). Only holds fields the user actually
  // changed, merged on top of the current props each render.
  const [drafts, setDrafts] = useState<Record<number, Partial<EditableRuleFields>>>({});

  // Auto-reveal a rule that appears after "+ Add rule" creates a blank one —
  // expand it so the structured editor is immediately visible for filling in.
  const currentRuleIds = pkg.rules.map((r) => r.id);
  const [prevRuleIds, setPrevRuleIds] = useState<number[]>(currentRuleIds);
  if (currentRuleIds.join(" ") !== prevRuleIds.join(" ")) {
    const added = currentRuleIds.filter((id) => !prevRuleIds.includes(id));
    setPrevRuleIds(currentRuleIds);
    if (added.length > 0) {
      setExpandedRuleIds((s) => {
        const next = new Set(s);
        added.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  const toggleEditPayorType = (value: string) => {
    setEditPayorTypes((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const resetEditState = () => {
    setEditName(pkg.name);
    setEditDescription(pkg.description);
    setEditInstructions(pkg.instructions);
    setEditPayorTypes(pkg.payor_types);
    setEditCategory(reportCategoryFromTypes(pkg.report_types));
  };

  const handleSaveEdit = async () => {
    await onUpdate({
      id: pkg.id,
      name: editName,
      description: editDescription,
      instructions: editInstructions,
      report_types: reportTypesFromCategory(editCategory),
      ...(pkg.package_type === PackageType.INSURANCE ? { payor_types: editPayorTypes } : {}),
    });
    setEditing(false);
  };

  const handleExportJson = () => {
    void navigator.clipboard.writeText(JSON.stringify(pkg, null, 2));
    notifySuccess("Copied package JSON to clipboard");
  };

  // ---- Rule editor plumbing ----

  const draftFor = (rule: QARule): EditableRuleFields => ({ ...deriveFields(rule), ...(drafts[rule.id] ?? {}) });
  const setDraft = (rule: QARule, patch: Partial<EditableRuleFields>) =>
    setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? {}), ...patch } }));

  const commit = (rule: QARule) => {
    // Blurring an unchanged field must not fire a write — every rule here is
    // editable (there's no ownership lock like the portal's org packages),
    // but a no-op blur shouldn't PATCH on every focus/blur cycle.
    const original = deriveFields(rule);
    const d = draftFor(rule);
    const unchanged =
      d.rule === original.rule &&
      d.location === original.location &&
      d.scanFor === original.scanFor &&
      d.feedback === original.feedback &&
      d.types.join() === original.types.join();
    if (unchanged) return;
    void onUpdateRule(
      rule.id,
      d.rule,
      serializeRuleBody({ types: d.types, location: d.location, scanFor: d.scanFor, feedback: d.feedback }),
    );
  };

  const toggleType = (rule: QARule, t: ReportType) => {
    const cur = draftFor(rule).types;
    const next = toggleReportType(cur, t);
    if (next === cur) return; // unchanged — would have dropped the last remaining type
    const nextFields = { ...draftFor(rule), types: next };
    setDraft(rule, { types: next });
    void onUpdateRule(
      rule.id,
      nextFields.rule,
      serializeRuleBody({
        types: nextFields.types,
        location: nextFields.location,
        scanFor: nextFields.scanFor,
        feedback: nextFields.feedback,
      }),
    );
  };

  const withRemoved = (set: Set<number>, id: number) => {
    const next = new Set(set);
    next.delete(id);
    return next;
  };
  const withAdded = (set: Set<number>, id: number) => new Set(set).add(id);
  const toggleRuleExpand = (id: number) =>
    setExpandedRuleIds((s) => (s.has(id) ? withRemoved(s, id) : withAdded(s, id)));
  const allExpanded = pkg.rules.length > 0 && pkg.rules.every((r) => expandedRuleIds.has(r.id));
  const toggleExpandAll = () => setExpandedRuleIds(allExpanded ? new Set() : new Set(pkg.rules.map((r) => r.id)));

  const typeBadge = TYPE_BADGE[pkg.package_type];
  const categoryBadge = CATEGORY_BADGE[reportCategoryFromTypes(pkg.report_types)];

  const menuItems: MenuItem[] = [
    {
      label: "Edit package details",
      onClick: () => {
        resetEditState();
        setEditing(true);
        setExpanded(true);
      },
    },
    { label: "Duplicate", onClick: () => void onDuplicate(pkg.id) },
    { label: "Export JSON", onClick: handleExportJson },
    {
      label: pkg.is_globally_disabled ? "Re-enable for all orgs" : "Disable for all orgs",
      onClick: () => void onToggleGlobal({ id: pkg.id, isGloballyDisabled: !pkg.is_globally_disabled }),
    },
    { label: "Delete", onClick: () => void onDelete(pkg.id), danger: true },
  ];

  return (
    <div
      className={clsx(
        "w-full rounded-lg border bg-bg-surface shadow-sm transition-colors",
        pkg.is_globally_disabled ? "border-border-subtle bg-bg-default" : "border-border-subtle",
      )}
    >
      {/* Package header */}
      <div className="flex items-start gap-3 px-4 py-4">
        <button
          type="button"
          className="mt-0.5 flex-shrink-0 text-text-tertiary hover:text-text-primary"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Collapse package" : "Expand package"}
        >
          {expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={clsx("font-semibold", pkg.is_globally_disabled ? "text-text-tertiary" : "text-text-primary")}
            >
              {pkg.name}
            </span>
            <Badge text={typeBadge.label} appearance={typeBadge.appearance} />
            <Badge text={categoryBadge.label} appearance={categoryBadge.appearance} />
            {pkg.is_globally_disabled && <Badge text="Disabled for all orgs" appearance="error" />}
            {pkg.payor_types.map((value) => (
              <Badge key={value} text={value} appearance="neutral" />
            ))}
            <Small colorType="secondary">
              {pkg.rules.length} rule{pkg.rules.length === 1 ? "" : "s"}
            </Small>
          </div>
          {pkg.description && (
            <Small colorType="secondary" className="mt-0.5 block">
              {pkg.description}
            </Small>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <KebabMenu ariaLabel="Package actions" items={menuItems} />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border-subtle">
          {/* Inline edit form */}
          {editing ? (
            <div className="m-4 flex flex-col gap-3 rounded border border-border-medium bg-bg-default p-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-text-secondary">
                  Name <span className="font-normal text-text-tertiary">— visible to users</span>
                </label>
                <input
                  className="w-full rounded border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-text-secondary">
                  Description <span className="font-normal text-text-tertiary">— visible to users</span>
                </label>
                <input
                  className="w-full rounded border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-text-secondary">
                  Instructions{" "}
                  <span className="font-normal text-text-tertiary">
                    (optional, LLM only) — use for guidance that doesn&apos;t fit a yes/no rule
                  </span>
                </label>
                <textarea
                  className="w-full rounded border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
                  rows={3}
                  value={editInstructions}
                  onChange={(e) => setEditInstructions(e.target.value)}
                />
              </div>
              {pkg.package_type === PackageType.INSURANCE && (
                <PayorTypeMultiSelect options={payorTypes} selected={editPayorTypes} onToggle={toggleEditPayorType} />
              )}
              <CategoryRadio name={`edit-package-${pkg.id}-category`} value={editCategory} onChange={setEditCategory} />
              <div className="flex gap-2">
                <Button appearance="primary" text="Save" onClick={handleSaveEdit} />
                <Button
                  appearance="secondary"
                  text="Cancel"
                  onClick={() => {
                    setEditing(false);
                    resetEditState();
                  }}
                />
              </div>
            </div>
          ) : (
            pkg.instructions && (
              <div className="m-4">
                <p className="mb-1 text-xs font-medium text-text-secondary">Instructions</p>
                <p className="text-sm text-text-primary">{pkg.instructions}</p>
              </div>
            )
          )}

          {/* Rules */}
          {pkg.rules.length > 0 && (
            <div className="flex items-center justify-end border-t border-border-subtle px-4 py-1.5 pr-6">
              <button
                type="button"
                onClick={toggleExpandAll}
                className="text-xs font-medium text-globemallow-700 hover:underline"
              >
                {allExpanded ? "Collapse all" : "Expand all"}
              </button>
            </div>
          )}

          {pkg.rules.length > 0 && (
            <div
              className={clsx(
                RULE_GRID,
                "border-t border-border-subtle bg-bg-default px-4 py-2 pl-6 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary",
              )}
            >
              <div>Rule name</div>
              <div>Report type</div>
              <div>Location in report</div>
              <div />
            </div>
          )}

          {pkg.rules.map((rule) => {
            const fields = draftFor(rule);
            return (
              <RuleItem
                key={rule.id}
                fields={fields}
                expanded={expandedRuleIds.has(rule.id)}
                handlers={{
                  onToggleExpand: () => toggleRuleExpand(rule.id),
                  onChangeField: (patch) => setDraft(rule, patch),
                  onToggleType: (t) => toggleType(rule, t),
                  onDelete: () => void onDeleteRule(rule.id),
                  onBlurSave: () => commit(rule),
                }}
              />
            );
          })}

          {pkg.rules.length === 0 && <p className="px-4 py-3 text-xs text-text-tertiary">No rules yet.</p>}

          <div className="border-t border-border-subtle px-4 py-2 pl-6">
            <button
              type="button"
              onClick={() => void onAddRule(pkg.id)}
              className="flex items-center gap-1 rounded px-2 py-1 text-sm font-medium text-globemallow-700 hover:bg-globemallow-50"
            >
              + Add rule
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
